/**
 * Copy trading, as a page of its own: who you copy, whom you invited to copy you, and — for whoever
 * runs the server — whether copied orders are really sent. Being copied is decided person by person:
 * you invite someone who has an account here; they start it themselves, with their own money.
 * The same body is the "Copy trading" section of Settings.
 */
import { useEffect, useState } from 'react'
import type { CopyConsent, CopyPerson, CopyRemoteState } from '@shared/copy'
import { copyApi } from './api'
import { Person, StartCopy, linkWords, useCopies } from './Copy'
import { Btn, Dialog, Field, Status, Switch, askSure, cx, inputCls, usd } from './kit'
import { Bar, Page, useTabs } from './Page'
import { Group, Row, Rows, note, quiet } from './rows'
import { useDemo } from './store'

const day = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
const fail = (e: unknown) => useDemo.getState().say(e instanceof Error ? e.message : 'That did not work')

/** Choose who may copy you: whoever runs the server picks from everyone here; anyone else names a person by their username. */
function InviteToCopy({ taken, onClose, onDone }: { taken: string[]; onClose: () => void; onDone: () => void }) {
  const say = useDemo((s) => s.say)
  const [people, setPeople] = useState<CopyPerson[] | null>(null)
  const [username, setUsername] = useState('')
  const [busy, setBusy] = useState(false)
  // being copied has terms of its own: read and accepted with every invitation
  const [terms, setTerms] = useState<CopyConsent | null>(null)
  const [agreed, setAgreed] = useState(false)
  useEffect(() => {
    void copyApi.people().then(setPeople, () => setPeople([]))
  }, [])
  const ready = !!terms && agreed && !busy
  const invite = (who: { userId?: string; username?: string }) => {
    if (!terms || !agreed) return
    setBusy(true)
    void copyApi.invite(who, terms.version).then(
      (p) => (say(`${p.name} can copy your trades now — they start it themselves`), onDone(), onClose()),
      (e) => (fail(e), setBusy(false)),
    )
  }
  const list = (people ?? []).filter((p) => !taken.includes(p.id))
  return (
    <Dialog
      open
      onClose={onClose}
      title="Invite someone to copy you"
      width={560}
      footer={
        <Btn variant="outline" onPanel onClick={onClose}>
          Close
        </Btn>
      }
    >
      <div className="space-y-4">
        <div className="text-[12px] leading-[18px] tracking-normal text-ws-fg-3">They see you under “People you copy” and start it themselves: with their own money, in their own account, by limits they set. You never see their money and place no order for them.</div>
        <LeaderTerms agreed={agreed} onAgreed={setAgreed} onTerms={setTerms} />
        {list.length > 0 && (
          <Rows>
            {list.map((p) => (
              <Row
                key={p.id}
                left={<Person name={p.name} />}
                title={p.name}
                sub={`@${p.username}`}
                right={
                  <button type="button" disabled={!ready} title={agreed ? undefined : 'Read and accept the terms above first'} className={cx(quiet, 'disabled:opacity-40')} onClick={() => invite({ userId: p.id })}>
                    Invite
                  </button>
                }
              />
            ))}
          </Rows>
        )}
        {people && !people.length && (
          <Field label="Their username here" hint="They need an account on this server">
            <div className="flex gap-2">
              <input value={username} onChange={(e) => setUsername(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && username.trim() && invite({ username })} autoComplete="off" spellCheck={false} placeholder="username" className={inputCls} />
              <Btn variant="white" className="shrink-0 px-3" disabled={!ready || !username.trim()} onClick={() => invite({ username })}>
                Invite
              </Btn>
            </div>
          </Field>
        )}
        {people && people.length > 0 && !list.length && <div className="text-[13px] tracking-normal text-ws-fg-3">Everyone with an account here is already invited.</div>}
      </div>
    </Dialog>
  )
}

/** The terms of being copied, read and ticked: the same for someone here and for someone on another server. */
function LeaderTerms({ agreed, onAgreed, onTerms }: { agreed: boolean; onAgreed: (v: boolean) => void; onTerms: (t: CopyConsent | null) => void }) {
  const [terms, setTerms] = useState<CopyConsent | null>(null)
  useEffect(() => {
    void copyApi.leaderTerms().then((t) => (setTerms(t), onTerms(t)), () => onTerms(null))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <h3 className="mb-[6px] text-[12px] font-normal leading-4 tracking-normal text-ws-label">What you are agreeing to</h3>
      <div className="max-h-[200px] overflow-y-auto whitespace-pre-wrap rounded-[8px] border border-ws-line px-3 py-[10px] text-[12px] leading-[18px] tracking-normal text-ws-fg-2">{terms?.text ?? 'Loading…'}</div>
      <label className="mt-3 flex cursor-pointer items-start gap-3 text-[13px] leading-5 tracking-normal text-ws-fg-2">
        <input type="checkbox" checked={agreed} onChange={(e) => onAgreed(e.target.checked)} className="mt-[3px] h-4 w-4 shrink-0 accent-white" />
        I have read this. I am not paid for being copied, I give no advice and I promise no result.
      </label>
    </div>
  )
}

/** Someone who runs their own Libera: a one-time code is made here and given to them; their server links to this one with it. */
function InviteRemote({ remote, onClose, onDone }: { remote: CopyRemoteState; onClose: () => void; onDone: () => void }) {
  const say = useDemo((s) => s.say)
  const [terms, setTerms] = useState<CopyConsent | null>(null)
  const [agreed, setAgreed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [code, setCode] = useState<{ code: string; expiresAt: number } | null>(null)
  const make = () => {
    if (!terms || !agreed) return
    setBusy(true)
    void copyApi.remoteInvite(terms.version).then(
      (c) => (setCode(c), setBusy(false), onDone()),
      (e) => (fail(e), setBusy(false)),
    )
  }
  return (
    <Dialog
      open
      onClose={onClose}
      title="Invite someone on another server"
      width={560}
      footer={
        <>
          <Btn variant="outline" onPanel onClick={onClose}>
            {code ? 'Done' : 'Cancel'}
          </Btn>
          {!code && remote.reachable && (
            <Btn variant="white" disabled={!terms || !agreed || busy} onClick={make}>
              {busy ? 'Making…' : 'Make a code'}
            </Btn>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {!remote.reachable ? (
          <div className="text-[13px] leading-5 tracking-normal text-ws-fg-2">
            This server ({remote.host}) has no address another server can reach, so nobody elsewhere can link to it. It needs a public https address — a domain with a certificate — set as <span className="text-ws-fg">PUBLIC_APP_URL</span>.
            <span className="mt-2 block text-ws-fg-3">Copying someone else from here needs none of that: only the server that is copied has to be reachable.</span>
          </div>
        ) : code ? (
          <>
            <div className="text-[13px] leading-5 tracking-normal text-ws-fg-2">Give this code to them, by any means. They enter it on their own server, under Copy trading → “Copy someone on another server”.</div>
            <textarea readOnly value={code.code} onFocus={(e) => e.currentTarget.select()} rows={4} className="w-full resize-none break-all rounded-[8px] border border-ws-line bg-ws-field px-3 py-2 font-mono text-[12px] leading-[18px] text-ws-fg" />
            {/* what the letters are: three things packed into one piece to paste — not a cipher */}
            <dl className="divide-y divide-ws-hair rounded-[8px] border border-ws-line px-3 text-[12px] leading-[18px] tracking-normal">
              <div className="flex justify-between gap-3 py-[6px]">
                <dt className="text-ws-fg-3">Where it leads</dt>
                <dd className="m-0 text-ws-fg">{remote.host} · this server</dd>
              </div>
              <div className="flex justify-between gap-3 py-[6px]">
                <dt className="text-ws-fg-3">What else is in it</dt>
                <dd className="m-0 text-right text-ws-fg-2">a one-time pass, and the mark of this server’s key</dd>
              </div>
              <div className="flex justify-between gap-3 py-[6px]">
                <dt className="text-ws-fg-3">How they connect</dt>
                <dd className="m-0 text-right text-ws-fg-2">their server straight to this one — through nobody else</dd>
              </div>
            </dl>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
                It works once, until {new Date(code.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, and whoever enters it first is linked: give it only to the person you mean. It can be cancelled while nobody has used it.
              </span>
              <Btn variant="outline" onPanel className="shrink-0" onClick={() => void navigator.clipboard?.writeText(code.code).then(() => say('Code copied'), () => say('Select the code and copy it'))}>
                Copy
              </Btn>
            </div>
          </>
        ) : (
          <>
            <div className="text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
              They run their own Libera, with their own broker connected there. Their server learns, for each order of yours: the stock, the direction, the time, your price and the share of your account it took — never your balances, your account or your other holdings. It places their orders itself; nothing about their money comes back here.
            </div>
            <LeaderTerms agreed={agreed} onAgreed={setAgreed} onTerms={setTerms} />
          </>
        )}
      </div>
    </Dialog>
  )
}

/** A code from someone who runs their own Libera: this server links to theirs. */
function LinkRemote({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const say = useDemo((s) => s.say)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')
  const link = () => {
    setBusy(true)
    setProblem('')
    void copyApi.remoteLink(code.trim()).then(
      (l) => (say(`Linked to ${l.name} on ${l.host} — start copying when you are ready`), onDone(), onClose()),
      (e) => (setProblem(e instanceof Error ? e.message : 'It could not be linked'), setBusy(false)),
    )
  }
  return (
    <Dialog
      open
      onClose={onClose}
      title="Copy someone on another server"
      width={520}
      footer={
        <>
          <Btn variant="outline" onPanel onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="white" disabled={busy || !code.trim()} onClick={link}>
            {busy ? 'Linking…' : 'Link'}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <div className="text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
          They made a code on their own server and gave it to you. This server links to theirs with it and from then on hears their orders — sealed for this server alone. Your broker stays connected here, on your own machine: their server never learns what you hold, how much you set aside or what you buy.
        </div>
        <Field label="Their code" hint="It starts with lbr1.">
          <textarea value={code} onChange={(e) => setCode(e.target.value)} rows={4} autoFocus spellCheck={false} placeholder="lbr1.…" className="w-full resize-none break-all rounded-[8px] border border-ws-line bg-ws-field px-3 py-2 font-mono text-[12px] leading-[18px] text-ws-fg placeholder:text-ws-fg-5 focus:border-ws-focus" />
        </Field>
        {problem && <p className="text-[12px] leading-[18px] tracking-normal text-ws-down">{problem}</p>}
      </div>
    </Dialog>
  )
}

/** The server's own switch, for whoever runs it: rehearsal (nothing is sent) or live. */
function CopyMode({ live, onChanged }: { live: boolean; onChanged: () => void }) {
  const say = useDemo((s) => s.say)
  const set = (on: boolean) => void copyApi.setMode(on).then(() => (say(on ? 'Copying is live: follower orders are sent' : 'Copying is in rehearsal: nothing is sent'), onChanged()), fail)
  return (
    <Group title="This server" note="Only you see this">
      <Rows>
        <Row
          title="Send copied orders for real"
          sub={live ? 'Live: a follower’s copy sends real orders to their own broker' : 'Rehearsal: every copied trade is worked out and written down, none is sent'}
          right={
            <Switch
              on={live}
              onChange={(v) => (v ? askSure({ title: 'Go live with copy trading?', body: 'From now on, when someone copies a trade, a real order goes to their own brokerage account. Watch a rehearsal first: a copy’s page shows what it would have sent.', cta: 'Go live', done: () => set(true) }) : set(false))}
              label="Send copied orders for real"
            />
          }
        />
      </Rows>
    </Group>
  )
}

const FOLLOWER: Record<string, { tone: 'free' | 'warn' | 'off'; text: string }> = { active: { tone: 'free', text: 'Copying' }, paused: { tone: 'off', text: 'Paused' }, stopping: { tone: 'warn', text: 'Stopping' }, stopped: { tone: 'off', text: 'Stopped' }, invited: { tone: 'off', text: 'Invited, not started' } }

/** Who you copy, whom you invited to copy you, how it works. */
export function CopyHub() {
  const { data, load } = useCopies()
  const admin = useDemo((s) => s.user?.role === 'admin')
  const openCopy = useDemo((s) => s.openCopy)
  const go = useDemo((s) => s.go)
  const [leader, setLeader] = useState<{ id: string; name: string } | null>(null)
  const [inviting, setInviting] = useState(false)
  const [linking, setLinking] = useState(false)
  const [invitingOut, setInvitingOut] = useState(false)
  if (!data) return <div className="h-24 animate-ws-pulse rounded-[8px] border border-ws-line" />
  const mine = data.copies.filter((c) => c.status !== 'stopped')
  const ended = data.copies.filter((c) => c.status === 'stopped')
  const free = data.leaders.filter((l) => !l.copying)
  // people on other servers I am linked to, and people there who are linked to me
  const remote = data.remote
  const far = remote?.leaders ?? []
  const farFree = far.filter((l) => !mine.some((c) => c.leader.id === l.id))
  const hostOf = (id: string) => far.find((l) => l.id === id)
  return (
    <>
      {!data.live && (
        <div className="rounded-[8px] border border-ws-line px-3 py-[10px] text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
          <span className="text-ws-warn">Rehearsal.</span> Copying is being tried out on this server: every copied trade is worked out and written down, and nothing is sent to a broker.
        </div>
      )}
      <Group title="People you copy" note="Each is its own sub-account in the portfolio">
        <Rows>
          {mine.map((c) => (
            <Row
              key={c.id}
              left={<Person name={c.leader.name} />}
              title={c.leader.name}
              sub={`${hostOf(c.leader.id) ? `On ${hostOf(c.leader.id)!.host} · ${linkWords(hostOf(c.leader.id)!)} · ` : ''}Since ${day(c.since)} · ${usd(c.putIn)} put in · worth ${usd(c.value)} · ${c.gain >= 0 ? '+' : '−'}${usd(Math.abs(c.gain))}`}
              right={<span className={cx('shrink-0 text-[12px] tracking-normal', c.gain > 0 ? 'text-ws-up' : c.gain < 0 ? 'text-ws-down' : 'text-ws-fg-3')}>{`${c.gainPct >= 0 ? '+' : '−'}${Math.abs(c.gainPct).toFixed(2)}%`}</span>}
              onClick={() => openCopy(c.id)}
            />
          ))}
          {free.map((l) => (
            <Row
              key={l.id}
              left={<Person name={l.name} />}
              title={l.name}
              sub="Invited you to copy their trades"
              right={
                <button type="button" className={quiet} onClick={() => setLeader(l)}>
                  Start copying
                </button>
              }
            />
          ))}
          {farFree.map((l) => (
            <Row
              key={l.id}
              left={<Person name={l.name} />}
              title={l.name}
              sub={`On ${l.host} · ${linkWords(l)}`}
              right={
                <span className="flex shrink-0 items-center gap-1">
                  {l.status !== 'gone' && (
                    <button type="button" className={quiet} onClick={() => setLeader(l)}>
                      Start copying
                    </button>
                  )}
                  <button type="button" className={quiet} onClick={() => askSure({ title: `Remove the link to ${l.name}?`, body: `This server stops listening to ${l.host}. To copy them again you need a new code from them.`, cta: 'Remove', done: () => void copyApi.remoteUnlink(l.id).then(load, fail) })}>
                    Remove
                  </button>
                </span>
              }
            />
          ))}
          {ended.map((c) => (
            <Row key={c.id} left={<Person name={c.leader.name} />} title={c.leader.name} sub={`Stopped · it made ${c.gain >= 0 ? '+' : '−'}${usd(Math.abs(c.gain))}`} right={<span className="shrink-0 text-[12px] tracking-normal text-ws-fg-4">Ended</span>} onClick={() => openCopy(c.id)} />
          ))}
          {!mine.length && !free.length && !ended.length && !farFree.length && <Row title="Nobody yet" sub="Someone who invites you to copy their trades shows up here" />}
          {remote && (
            <Row
              title="Copy someone on another server"
              sub="They run their own Libera and gave you a code"
              right={
                <button type="button" className={quiet} onClick={() => setLinking(true)}>
                  Enter a code
                </button>
              }
            />
          )}
        </Rows>
        <p className={note}>You choose how much money follows a person and your own limits. That money is kept apart from the rest of your account, and you can stop at any time.</p>
      </Group>

      <Group title="People who may copy you" note="You choose them, one by one">
        <Rows>
          {data.followers.map((f) => (
            <Row
              key={f.id}
              left={<Person name={f.name} />}
              title={f.name}
              sub={f.status === 'invited' ? `Invited ${day(f.since)}` : `Copying since ${day(f.since)}`}
              right={
                <span className="flex shrink-0 items-center gap-2">
                  <Status tone={FOLLOWER[f.status].tone}>{FOLLOWER[f.status].text}</Status>
                  <button
                    type="button"
                    className={quiet}
                    onClick={() =>
                      askSure({
                        title: `Take back ${f.name}’s invitation?`,
                        body: f.status === 'invited' ? 'They can no longer start copying you.' : 'Nothing new you buy is copied by them. What their copy already holds is theirs: your sales are still mirrored there, and they can stop it whenever they want.',
                        cta: 'Take back',
                        done: () => void copyApi.uninvite(f.id).then(load, fail),
                      })
                    }
                  >
                    Take back
                  </button>
                </span>
              }
            />
          ))}
          {(remote?.peers ?? []).map((p) => (
            <Row
              key={p.id}
              left={<Person name={p.name} />}
              title={p.name}
              sub={`On ${p.host ?? 'their own server'} · linked ${day(p.since)}${p.connected ? '' : p.seenAt ? ` · last heard ${new Date(p.seenAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ' · not heard from yet'}`}
              right={
                <span className="flex shrink-0 items-center gap-2">
                  <Status tone={p.status === 'off' ? 'off' : p.connected ? 'free' : 'warn'}>{p.status === 'off' ? 'Taken back' : p.connected ? 'Linked' : 'Not connected'}</Status>
                  {p.status === 'off' ? (
                    <button type="button" className={quiet} onClick={() => void copyApi.remotePeer(p.id, 'active').then(load, fail)}>
                      Give back
                    </button>
                  ) : (
                    <button type="button" className={quiet} onClick={() => askSure({ title: `Take back ${p.name}’s link?`, body: 'Your new buys no longer reach their server. Your sales still do, so that their copy is not left holding what you sold; they can stop it whenever they want.', cta: 'Take back', done: () => void copyApi.remotePeer(p.id, 'off').then(load, fail) })}>
                      Take back
                    </button>
                  )}
                  <button type="button" className={quiet} onClick={() => askSure({ title: `Remove ${p.name}’s link?`, body: 'Their server hears nothing from this one any more — your sales neither. A copy of you that still runs there is on its own from then on; they are told.', cta: 'Remove', done: () => void copyApi.remotePeer(p.id, 'gone').then(load, fail) })}>
                    Remove
                  </button>
                </span>
              }
            />
          ))}
          <Row
            title="Invite someone to copy you"
            sub="Someone who already has an account here"
            right={
              <button type="button" className={quiet} onClick={() => setInviting(true)}>
                Invite
              </button>
            }
          />
          {remote && (
            <Row
              title="Invite someone on another server"
              sub={remote.reachable ? `They run their own Libera, with their own broker there${remote.openInvites ? ` · ${remote.openInvites} code${remote.openInvites === 1 ? '' : 's'} not used yet` : ''}` : `This server (${remote.host}) cannot be reached from outside: it can copy others, but not be copied from elsewhere`}
              right={
                <span className="flex shrink-0 items-center gap-1">
                  {remote.openInvites > 0 && (
                    <button
                      type="button"
                      className={quiet}
                      onClick={() =>
                        askSure({
                          title: remote.openInvites === 1 ? 'Cancel the code nobody has used?' : `Cancel the ${remote.openInvites} codes nobody has used?`,
                          body: 'A code that is cancelled links nobody. Links that already stand are not touched.',
                          cta: 'Cancel it',
                          done: () => void copyApi.remoteCancelInvites().then(load, fail),
                        })
                      }
                    >
                      Cancel
                    </button>
                  )}
                  <button type="button" className={quiet} onClick={() => setInvitingOut(true)}>
                    {remote.reachable ? 'Make a code' : 'Why'}
                  </button>
                </span>
              }
            />
          )}
        </Rows>
        <p className={note}>
          You never see what they hold or how much they put in, and you place no order for them: their own account copies yours, by rules they set and can stop at any time.
          {admin && (
            <>
              {' '}
              Someone who has no account yet gets one in{' '}
              <button type="button" className="underline decoration-ws-rule underline-offset-2 hover:text-ws-fg" onClick={() => (go('settings'), useTabs.getState().set({ settings: 'people' }))}>
                Settings → People
              </button>
              .
            </>
          )}
        </p>
      </Group>

      <Group title="How it works">
        <Rows>
          <Row left={<span className="w-4 shrink-0 text-center text-[12px] tabular-nums text-ws-fg-4">1</span>} title="The one to be copied invites the other" sub="Above: “Invite someone to copy you”. Both need an account here and a broker of their own" />
          <Row left={<span className="w-4 shrink-0 text-center text-[12px] tabular-nums text-ws-fg-4">2</span>} title="The one who copies starts it" sub="Under “People you copy”: Start copying, with an amount of their own money and their own limits" />
          <Row left={<span className="w-4 shrink-0 text-center text-[12px] tabular-nums text-ws-fg-4">·</span>} title="Each on their own server" sub="Two people who each run their own Libera link the two with a one-time code. Brokers stay where they are; only orders cross, sealed, from the one who is copied to the one who copies" />
          <Row left={<span className="w-4 shrink-0 text-center text-[12px] tabular-nums text-ws-fg-4">3</span>} title="From then on it follows by itself" sub="A buy takes the same share of the copy’s money as it took of the copied account; a sale sells the same share of what the copy holds" />
        </Rows>
      </Group>
      {admin && <CopyMode live={data.live} onChanged={load} />}
      <StartCopy leader={leader} free={data.free} onClose={() => setLeader(null)} onStarted={load} />
      {linking && <LinkRemote onClose={() => setLinking(false)} onDone={load} />}
      {invitingOut && remote && <InviteRemote remote={remote} onClose={() => setInvitingOut(false)} onDone={load} />}
      {inviting && <InviteToCopy taken={data.followers.map((f) => f.id)} onClose={() => setInviting(false)} onDone={load} />}
    </>
  )
}

export const CopyBar = () => <Bar>{<span className="px-2 text-[13px] tracking-normal text-ws-fg-3">Follow someone’s trades with your own money and your own limits</span>}</Bar>
export function CopyView() {
  return (
    <Page className="mx-auto max-w-[720px]">
      <div className="space-y-6">
        <CopyHub />
      </div>
    </Page>
  )
}
